import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { useFetch, Card, Loader, PageHeader } from "../ui";

const LEVEL = { GREEN: "bg-[#C6FF00]/10 text-[#C6FF00]", YELLOW: "bg-amber-500/15 text-amber-400", RED: "bg-red-500/15 text-red-400" };

function DigestPanel() {
  const [sections, setSections] = useState(null);
  const [sendingWins, setSendingWins] = useState(false);
  useEffect(() => { api.get("/admin/delivery/digest-config").then(({ data }) => setSections(data.sections)).catch(() => {}); }, []);

  const toggleSection = async (key, enabled) => {
    setSections((s) => s.map((x) => (x.key === key ? { ...x, enabled } : x)));
    try { await api.put("/admin/delivery/digest-config", { sections: { [key]: enabled } }); toast.success(enabled ? "Section added to tomorrow's digest" : "Section removed from the digest"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const sendWins = async () => {
    setSendingWins(true);
    try { await api.post("/admin/delivery/weekly-wins/send"); toast.success("Weekly Wins recap sent to your inbox"); }
    catch (e) { toast.error(apiError(e)); }
    setSendingWins(false);
  };

  return (
    <Card className="p-5" data-testid="digest-config-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-bold text-white">Daily Digest Contents</p>
          <p className="text-xs text-[#8A939B]">Choose what lands in your 7am email. Weekly Wins goes out every Friday at 3pm ET.</p>
        </div>
        <button data-testid="send-weekly-wins-btn" onClick={sendWins} disabled={sendingWins}
          className="rounded-full border border-[#23262c] px-4 py-2 text-xs font-bold text-[#8A939B] transition-colors hover:border-[#C6FF00] hover:text-white disabled:opacity-50">
          {sendingWins ? "Sending…" : "Send Weekly Wins now →"}
        </button>
      </div>
      {!sections ? <p className="mt-3 text-sm text-[#6B7280]">Loading…</p> : (
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {sections.map((s) => (
            <label key={s.key} data-testid={`digest-section-${s.key}`} className="flex cursor-pointer items-center justify-between gap-3 rounded-xl bg-[#0E1116] px-3.5 py-2.5">
              <span className="text-sm text-[#c8cdd3]">{s.label}</span>
              <button data-testid={`digest-toggle-${s.key}`} onClick={(e) => { e.preventDefault(); toggleSection(s.key, !s.enabled); }}
                className={`h-5 w-9 shrink-0 rounded-full p-0.5 transition-colors ${s.enabled ? "bg-[#C6FF00]" : "bg-[#23262c]"}`}>
                <span className={`block h-4 w-4 rounded-full bg-[#0A0C0F] transition-transform ${s.enabled ? "translate-x-4" : ""}`} />
              </button>
            </label>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function Automations() {
  const { data, loading, reload } = useFetch("/admin/delivery/automations", []);
  const { data: logsData } = useFetch("/admin/delivery/automation-logs", []);
  if (loading || !data) return <Loader />;
  const h = data.health;

  const toggle = async (key, enabled) => {
    try { await api.put(`/admin/delivery/automations/${key}`, { enabled }); toast.success(enabled ? "Automation enabled" : "Automation disabled"); reload(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Delivery OS" title="Automations" subtitle="Every automated behavior in one place — green runs itself, yellow prepares for your approval, red never executes without you."
        actions={<a href="/admin/email-templates" data-testid="go-email-templates" className="rounded-full border border-[#23262c] px-4 py-2 text-xs font-bold text-[#8A939B] transition-colors hover:border-[#C6FF00] hover:text-white">Edit Email Templates →</a>} />
      <div className="grid grid-cols-3 gap-3" data-testid="automation-health">
        {[["Runs (24h)", h.run_today], ["Failed (24h)", h.failed_today], ["Emails Sent (24h)", h.emails_sent_today]].map(([k, v]) => (
          <Card key={k} className="p-4 text-center"><p className={`text-2xl font-extrabold ${k.includes("Failed") && v > 0 ? "text-red-400" : "text-white"}`}>{v}</p><p className="mt-1 text-[10px] font-bold uppercase text-[#8A939B]">{k}</p></Card>
        ))}
      </div>
      <DigestPanel />
      <div className="space-y-2">
        {data.rules.map((r) => (
          <Card key={r.key} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`automation-${r.key}`}>
            <div>
              <p className="font-bold text-white">{r.name}</p>
              <p className="text-xs text-[#8A939B]">Event: {r.event}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`rounded-full px-2.5 py-1 text-[10px] font-extrabold ${LEVEL[r.level]}`}>{r.level}</span>
              {r.level === "RED" ? (
                <span className="text-xs font-bold text-[#8A939B]">HUMAN ONLY</span>
              ) : (
                <button data-testid={`automation-toggle-${r.key}`} onClick={() => toggle(r.key, !r.enabled)}
                  className={`h-6 w-11 rounded-full p-0.5 transition-colors ${r.enabled ? "bg-[#C6FF00]" : "bg-[#23262c]"}`}>
                  <span className={`block h-5 w-5 rounded-full bg-[#0A0C0F] transition-transform ${r.enabled ? "translate-x-5" : ""}`} />
                </button>
              )}
            </div>
          </Card>
        ))}
      </div>
      <Card className="p-5" data-testid="automation-logs">
        <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Recent Automation Activity</p>
        {(!logsData || logsData.logs.length === 0) && <p className="text-sm text-[#6B7280]">No automation activity yet — actions will be logged here as they run.</p>}
        {(logsData?.logs || []).slice(0, 30).map((l) => (
          <p key={l.id} className="mt-1.5 text-sm text-[#c8cdd3]">
            <span className="text-[#8A939B]">{new Date(l.at).toLocaleString()}</span> — <span className="font-bold text-white">{l.key}</span> <span className={l.status === "failed" ? "text-red-400 font-bold" : "text-[#C6FF00]"}>{l.status}</span> {l.detail && <span className="text-xs text-[#6B7280]">· {l.detail}</span>}
          </p>
        ))}
      </Card>
    </div>
  );
}
